import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Lock, FileText } from "lucide-react";
import { http, trackEvent } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { Card, PageTitle, StatusPill, btnPrimary } from "@/components/portal/ui";

// My Blueprint hub — eligibility + status, routing to existing blueprint intake/view pages.
export default function MyBlueprint() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { http.get("/portal/overview").then((r) => { setData(r.data); if (r.data.clarity_completed) trackEvent("blueprint_started", {}); }).catch(() => setData({ services: [] })); }, []);
  if (!data) return <div className="text-[#6B665E]">Loading…</div>;

  const clarityDone = data.clarity_completed;
  const bp = (data.services || []).find((s) => s.product_key === "blueprint");

  // Locked until Clarity is complete.
  if (!clarityDone) {
    return (
      <div>
        <PageTitle eyebrow="Your Journey" title="My Blueprint" sub="Your complete personalized sleep plan." />
        <Card className="p-8 text-center" data-testid="blueprint-locked">
          <Lock className="mx-auto text-[#8a6a2e]" size={34} />
          <p className="font-display text-2xl text-[#1A1918] mt-4">Available after Clarity</p>
          <p className="text-[#6B665E] mt-2 text-sm max-w-md mx-auto">Your Blueprint is built from what your Certified Infant Sleep Consultant learns during Clarity, so every family completes the Infant Sleep Diagnostic first.</p>
          <button data-testid="blueprint-go-clarity" onClick={() => nav("/portal/clarity")} className={`${btnPrimary} mt-6`}>Go to My Clarity</button>
        </Card>
      </div>
    );
  }

  // Clarity done but Blueprint not yet purchased → offer to add it.
  if (!bp) {
    return (
      <div>
        <PageTitle eyebrow="Your Journey" title="My Blueprint" sub="Your complete personalized sleep plan." />
        <Card className="p-8 text-center" data-testid="blueprint-unlock">
          <FileText className="mx-auto text-[#9F634E]" size={34} />
          <p className="font-display text-2xl text-[#1A1918] mt-4">Create your Personalized Blueprint</p>
          <p className="text-[#6B665E] mt-2 text-sm max-w-md mx-auto">You've completed Clarity. Add the Blueprint and your Certified Infant Sleep Consultant turns your priorities into a complete step-by-step plan.</p>
          <button data-testid="blueprint-add" onClick={() => startCheckout({ product: "blueprint", user, navigate: nav })} className={`${btnPrimary} mt-6`}>Create My Blueprint — $279</button>
        </Card>
      </div>
    );
  }

  const st = bp.state;
  const intakeDone = ["Awaiting Operator", "In Preparation", "Ready", "Delivered", "Viewed", "Completed"].includes(st);
  const ready = ["Ready", "Delivered", "Viewed", "Completed"].includes(st);

  return (
    <div>
      <PageTitle eyebrow="Your Journey" title="My Blueprint" sub="Your complete personalized sleep plan, created by your Certified Infant Sleep Consultant." />
      <div className="flex items-center gap-3 mb-5"><span className="text-sm text-[#8A998C]">Current status:</span><StatusPill state={st} /></div>

      <div className="flex items-start gap-2 bg-[#667A66]/10 border border-[#667A66]/20 rounded-lg px-4 py-3 mb-5 max-w-2xl" data-testid="blueprint-call-included-note">
        <CheckCircle2 size={16} className="text-[#4d6b4d] mt-0.5 shrink-0" />
        <p className="text-sm text-[#4d6b4d]">Your Blueprint includes a complimentary, optional 45-minute walkthrough call with Andrea Hurley — a one-time review, not ongoing coaching. The booking button appears {ready ? "with your Blueprint below." : "here once your Blueprint is ready."}</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        <Card className="p-6 flex items-center justify-between gap-4" data-testid="blueprint-intake-card">
          <div>
            <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1">{intakeDone ? <CheckCircle2 size={13} className="text-[#4d6b4d]" /> : <Clock size={13} />} Step 1 · Blueprint Intake</div>
            <p className="font-display text-lg text-[#1A1918]">The full planning intake</p>
            <p className="text-[#6B665E] text-sm">{intakeDone ? "Submitted — your Clarity answers were carried forward." : "We carry your Clarity answers forward; you only add the deeper planning details."}</p>
          </div>
          {!intakeDone && <button data-testid="blueprint-go-intake" onClick={() => nav("/portal/blueprint-intake")} className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}>Continue <ArrowRight size={15} /></button>}
        </Card>

        <Card className="p-6" data-testid="blueprint-prep-card">
          <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1">{ready ? <CheckCircle2 size={13} className="text-[#4d6b4d]" /> : <Clock size={13} />} Step 2 · Consultant Preparation</div>
          <p className="font-display text-lg text-[#1A1918]">Your plan is being built</p>
          <p className="text-[#6B665E] text-sm">{ready ? "Complete — your Blueprint is ready below." : intakeDone ? "Your Certified Infant Sleep Consultant is preparing your personalized plan." : "Begins once your intake is submitted."}</p>
        </Card>

        <Card className="p-6 flex items-center justify-between gap-4" data-testid="blueprint-view-card">
          <div>
            <div className="flex items-center gap-2 text-[#8A998C] text-xs mb-1"><FileText size={13} /> Step 3 · Your Blueprint</div>
            <p className="font-display text-lg text-[#1A1918]">Personalized Infant Sleep Blueprint</p>
            <p className="text-[#6B665E] text-sm">{ready ? "Ready to view." : "Appears here once your Certified Infant Sleep Consultant completes it."}</p>
          </div>
          {ready && <button data-testid="blueprint-view" onClick={() => nav("/portal/blueprint")} className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}>View Blueprint <ArrowRight size={15} /></button>}
        </Card>
      </div>
    </div>
  );
}
